/**
 * ASC 606 Phase 11g — Right-to-Invoice Expedient toggle (55-18).
 *
 * Per-POB reviewer-facing flip for ASC 606-10-55-18 practical expedient.
 * When enabled, subsequent milestone / unit-delivery events that carry an
 * invoice_amount recognize the invoiced amount directly (no pct math).
 *
 * Post-approval blocked by backend with HTTP 409 MODIFICATION_WIZARD_REQUIRED
 * — shown inline with a wizard-pointer when the contract is ACTIVE.
 *
 * Backend: POST /api/v1/revenue/contracts/{id}/pobs/{pob_id}/right-to-invoice-expedient
 */
import { useState } from 'react';
import { API_V1, authFetch } from '../../constants';

const API = `${API_V1}/revenue`;

interface Props {
  contractId: number;
  pobId: number;
  currentEnabled: boolean;
  currentRationale: string | null;
  contractStatus: string;
  onUpdated?: (enabled: boolean, rationale: string) => void;
}

export default function RightToInvoicePanel({
  contractId,
  pobId,
  currentEnabled,
  currentRationale,
  contractStatus,
  onUpdated,
}: Props) {
  const [enabled, setEnabled] = useState(currentEnabled);
  const [rationale, setRationale] = useState(currentRationale || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const isActive = (contractStatus || '').toUpperCase() === 'ACTIVE';
  const dirty = enabled !== currentEnabled || rationale !== (currentRationale || '');

  const submit = async () => {
    setError(null);
    setFlash(null);
    if (rationale.trim().length < 30) {
      setError('Rationale must be at least 30 characters (SOX 404 audit trail).');
      return;
    }
    setSubmitting(true);
    try {
      const res = await authFetch(
        `${API}/contracts/${contractId}/pobs/${pobId}/right-to-invoice-expedient`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabled, rationale: rationale.trim() }),
        },
      );
      if (res.ok) {
        const data = await res.json();
        setFlash(`R2I expedient ${data.right_to_invoice_expedient ? 'ENABLED' : 'DISABLED'}.`);
        if (onUpdated) onUpdated(data.right_to_invoice_expedient, data.right_to_invoice_rationale);
      } else {
        const err = await res.json().catch(() => ({ detail: 'Unknown error' }));
        const detail = typeof err.detail === 'string' ? err.detail : err.detail?.message || JSON.stringify(err.detail);
        setError(detail || `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setError(e?.message || 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        border: '1px solid var(--border-subtle)',
        borderRadius: 6,
        padding: 14,
        background: 'var(--bg-surface)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
            Right-to-Invoice Practical Expedient
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
            ASC 606-10-55-18 — recognize invoice amount directly when it corresponds to value transferred.
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: isActive ? 'not-allowed' : 'pointer' }}>
          <input
            type="checkbox"
            checked={enabled}
            disabled={isActive || submitting}
            onChange={(e) => setEnabled(e.target.checked)}
            style={{ width: 16, height: 16 }}
          />
          <span style={{ fontSize: 12, fontWeight: 600 }}>{enabled ? 'ENABLED' : 'DISABLED'}</span>
        </label>
      </div>
      <textarea
        value={rationale}
        disabled={isActive || submitting}
        onChange={(e) => setRationale(e.target.value)}
        placeholder="Rationale (≥30 chars) — why the invoice amount corresponds directly to value transferred..."
        style={{
          width: '100%',
          padding: 6,
          fontSize: 12,
          background: 'var(--bg-card)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 4,
          color: 'var(--text-primary)',
          resize: 'vertical',
          minHeight: 48,
          fontFamily: 'inherit',
        }}
      />
      {error && (
        <div
          style={{
            marginTop: 8,
            padding: 8,
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid var(--negative)',
            borderRadius: 4,
            fontSize: 12,
            color: 'var(--negative)',
          }}
        >
          {error}
        </div>
      )}
      {flash && (
        <div
          style={{
            marginTop: 8,
            padding: 8,
            background: 'rgba(34,197,94,0.1)',
            border: '1px solid var(--positive)',
            borderRadius: 4,
            fontSize: 12,
            color: 'var(--positive)',
          }}
        >
          {flash}
        </div>
      )}
      {isActive && (
        <div
          style={{
            marginTop: 8,
            fontSize: 11,
            color: 'var(--text-secondary)',
            fontStyle: 'italic',
          }}
        >
          Contract is ACTIVE — R2I toggle disabled. Route post-approval changes through the modification wizard (Phase 7d).
        </div>
      )}
      {!isActive && (
        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={submit}
            disabled={!dirty || submitting || rationale.trim().length < 30}
            style={{
              padding: '6px 14px',
              fontSize: 12,
              fontWeight: 600,
              background: dirty && rationale.trim().length >= 30 ? 'var(--accent)' : 'var(--bg-hover)',
              color: dirty && rationale.trim().length >= 30 ? 'white' : 'var(--text-hint)',
              border: 'none',
              borderRadius: 4,
              cursor: submitting ? 'wait' : dirty ? 'pointer' : 'not-allowed',
            }}
          >
            {submitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
}
